import {
  inflationFactorFromAnchor,
  withdrawalInflationFactor,
} from './inflationPath';

/** @typedef {{ fromAge: number, toAge: number, net: number, inflate?: boolean }} IncomeOverride */

export const DRAWDOWN_NET_MAX = 250000;

/**
 * Clamp and sort custom target periods (storage / share-link shape).
 * @param {IncomeOverride[]} overrides
 * @param {{ currentAge: number, lifeExpectancy: number, retirementAge: number }} bounds
 */
export function normalizeIncomeOverrides(overrides, { currentAge, lifeExpectancy, retirementAge }) {
  if (!Array.isArray(overrides) || overrides.length === 0) return [];

  const minFrom = retirementAge ?? currentAge;

  return overrides
    .map((row) => {
      const from = Math.round(Number(row.fromAge));
      const to = Math.round(Number(row.toAge));
      const lo = Math.max(minFrom, Math.min(from, to));
      const hi = Math.min(lifeExpectancy, Math.max(from, to));
      const multiYear = hi > lo;
      return {
        fromAge: lo,
        toAge: hi,
        net: Math.max(0, Math.min(DRAWDOWN_NET_MAX, Math.round(Number(row.net) || 0))),
        inflate: multiYear ? row.inflate === true : true,
      };
    })
    .filter((row) => row.fromAge <= row.toAge)
    .sort((a, b) => a.fromAge - b.fromAge);
}

/** @param {IncomeOverride} a @param {IncomeOverride} b */
export function incomeOverridesOverlap(a, b) {
  return a.fromAge <= b.toAge && b.fromAge <= a.toAge;
}

/**
 * Validate custom drawdown rows. Returns only rows that pass every rule when `isValid`.
 * @param {IncomeOverride[]} overrides
 * @param {{ retirementAge: number, lifeExpectancy: number, netMax?: number }} bounds
 */
export function validateDrawdownYears(overrides, {
  retirementAge,
  lifeExpectancy,
  netMax = DRAWDOWN_NET_MAX,
}) {
  if (!Array.isArray(overrides) || overrides.length === 0) {
    return { effectiveRows: [], rowIssues: [], globalError: '', isValid: true };
  }

  /** @type {{ index: number, issues: string[] }[]} */
  const rowIssues = [];
  /** @type {(IncomeOverride & { index: number })[]} */
  const parsed = [];

  overrides.forEach((row, index) => {
    const issues = [];
    const fromAge = Math.round(Number(row.fromAge));
    const toAge = Math.round(Number(row.toAge));
    const net = Math.round(Number(row.net) || 0);
    const lo = Math.min(fromAge, toAge);
    const hi = Math.max(fromAge, toAge);

    if (!Number.isFinite(fromAge) || !Number.isFinite(toAge)) {
      issues.push('Enter valid ages.');
    }
    if (lo < retirementAge) {
      issues.push(`From must be ${retirementAge} or later (drawdown only).`);
    }
    if (hi > lifeExpectancy) {
      issues.push(`To cannot be after plan age ${lifeExpectancy}.`);
    }
    if (lo > lifeExpectancy) {
      issues.push(`Period falls after plan age ${lifeExpectancy}.`);
    }
    if (net <= 0) {
      issues.push('Enter net income above £0.');
    }
    if (net > netMax) {
      issues.push(`Net cannot exceed £${netMax.toLocaleString()}.`);
    }

    if (issues.length > 0) {
      rowIssues.push({ index, issues });
      return;
    }

    parsed.push({
      index,
      fromAge: lo,
      toAge: hi,
      net,
      inflate: hi > lo ? row.inflate === true : true,
    });
  });

  parsed.sort((a, b) => a.fromAge - b.fromAge);

  let globalError = '';
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      if (incomeOverridesOverlap(parsed[i], parsed[j])) {
        globalError = 'Age ranges cannot overlap.';
        break;
      }
    }
    if (globalError) break;
  }

  const isValid = rowIssues.length === 0 && !globalError;
  const effectiveRows = isValid
    ? parsed.map(({ index, ...row }) => row)
    : [];

  return { effectiveRows, rowIssues, globalError, isValid };
}

/**
 * @param {IncomeOverride[]} overrides
 * @param {number} age
 * @returns {IncomeOverride | undefined}
 */
export function findOverrideForAge(overrides, age) {
  return overrides.find((row) => age >= row.fromAge && age <= row.toAge);
}

/**
 * Target net income for a plan year — default slider + global inflation, or a custom period.
 */
export function resolveTargetNetAtAge({
  age,
  baseTarget,
  inflateWithdrawals,
  retirementAge,
  currentAge,
  overrides = [],
  pathMap,
  flatInflationPct,
  baseCalendarYear = new Date().getFullYear(),
}) {
  const override = findOverrideForAge(overrides, age);
  if (override) {
    if (override.toAge <= override.fromAge || !override.inflate) {
      return override.net;
    }
    return override.net * inflationFactorFromAnchor({
      age,
      anchorAge: override.fromAge,
      currentAge,
      pathMap,
      flatInflationPct,
      baseCalendarYear,
    });
  }

  if (age < retirementAge || !inflateWithdrawals || age <= retirementAge) {
    return baseTarget;
  }

  return baseTarget * withdrawalInflationFactor({
    age,
    retirementAge,
    currentAge,
    pathMap,
    flatInflationPct,
    inflateWithdrawals,
    baseCalendarYear,
  });
}

/** Compact tuples for share links: [from, to, net] or [from, to, net, 1] when inflate on. */
export function encodeIncomeOverridesForShare(overrides) {
  if (!Array.isArray(overrides) || overrides.length === 0) return undefined;

  return overrides.map((row) => {
    const tuple = [row.fromAge, row.toAge, row.net];
    if (row.toAge > row.fromAge && row.inflate) tuple.push(1);
    return tuple;
  });
}

/** @param {unknown} encoded */
export function decodeIncomeOverridesFromShare(encoded) {
  if (!Array.isArray(encoded)) return [];
  return encoded
    .filter((row) => Array.isArray(row) && row.length >= 3)
    .map((row) => {
      const fromAge = Math.round(Number(row[0]));
      const toAge = Math.round(Number(row[1]));
      const net = Math.round(Number(row[2]));
      const multiYear = toAge > fromAge;
      return {
        fromAge,
        toAge,
        net,
        inflate: multiYear ? row[3] === 1 : true,
      };
    });
}

export function formatOverrideSummary(row) {
  const ageLabel = row.fromAge === row.toAge
    ? `Age ${row.fromAge}`
    : `Ages ${row.fromAge}–${row.toAge}`;
  const netLabel = `£${row.net.toLocaleString()}/yr net`;
  if (row.toAge > row.fromAge && row.inflate) {
    return `${ageLabel} · ${netLabel} (rising with inflation)`;
  }
  return `${ageLabel} · ${netLabel}`;
}
